import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

/**
 * #1046 — branded landing page for invite emails.
 *
 * The invite template links here with `token_hash` and `type=invite` instead of
 * GoTrue's own GET verify URL. Nothing is verified on load: email scanners that
 * prefetch the link would otherwise spend the single-use token before the
 * invitee clicks. The token is only sent when the person presses the button.
 */
export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once, then cleared from the URL below so the live token doesn't sit in
  // the address bar or history. Sentry's pageload transaction, navigation
  // breadcrumb and replay buffer can still hold the original URL.
  const [{ tokenHash, type }] = useState(() => ({
    tokenHash: searchParams.get("token_hash"),
    type: searchParams.get("type"),
  }));
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (searchParams.has("token_hash") || searchParams.has("type")) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const goToLogin = () => navigate("/login");

  if (!tokenHash || type !== "invite") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="font-display">Invalid Link</CardTitle>
            <CardDescription>
              This link is invalid or has expired. If you came from an invite email, open the link in the email again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={goToLogin} className="w-full">Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleAccept = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "invite" });
      if (error) {
        setFailed(true);
        return;
      }
      // ResetPasswordPage shows "Set Your Password" when the hash carries
      // type=invite, and verifyOtp has just signed the invitee in for updateUser.
      navigate("/reset-password#type=invite", { replace: true });
    } catch {
      // A network failure leaves the token unspent, so the button stays usable.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-display">You're invited</CardTitle>
          <CardDescription>Accept your invite to set a password and get started.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {failed && (
            <p role="alert" className="text-sm text-destructive">
              We couldn't accept this invite. The link may have expired or already been used. Ask whoever invited you
              to resend it, or sign in if you've already set a password.
            </p>
          )}
          <Button onClick={handleAccept} className="w-full" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Accept invite
          </Button>
          {failed && (
            <Button variant="outline" onClick={goToLogin} className="w-full">Go to Login</Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
